import { useMutation, useReactiveVar } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ButtonDock, liquidGlassRowShadowSx, liquidGlassSurfaceFillSx, miniButtonDockScrollBottomPad } from '@focuslee/shared-mini-app';
import { loginData } from '../client/apollo';
import { QuestDifficulty, QuestStatus, RepeatingType, RitualType } from '../client/generated/graphql';
import { createQuestCache, createQuestMutation } from '../client/mutations/createQuest';
import { updateQuestMutation } from '../client/mutations/updateQuest';
import { updateUserMutation } from '../client/mutations/updateUser';
import { getUserRitualsQuery } from '../client/queries/userRituals';
import { Page } from '../components/Page';
import { CommonIcon } from '../components/common/CommonIcon';
import { Switch } from '../components/common/Switch';
import { RitualTaskItem } from '../components/ritual/RitualTaskItem';
import { RitualEditPageSkeleton } from '../components/ritual/skeleton/RitualEditPageSkeleton';
import { getToday } from '../helpers/datetime';
import { getRitualConfig } from '../helpers/ritual';
import { useCachedQuestWithFallback } from '../hooks/caching/quest';
import { useCachedRitualQuestIds } from '../hooks/caching/user';
import { ritualTaskEditPath } from '../navigation/paths';

const getSystemCategoryId = (userId?: string | null) =>
    userId ? `${userId}_system-focuslee` : null;

const toUpdateQuestVariables = (
    ritual: NonNullable<ReturnType<typeof useCachedQuestWithFallback>['quest']>,
    overrides: Record<string, unknown> = {},
) => {
    const {
        id: questId,
        __typename,
        metric,
        tracking,
        completedOn,
        templateId,
        ritualType,
        repeat,
        subTasks,
        ...questFields
    } = ritual;

    return {
        ...questFields,
        questId,
        repeat: repeat
            ? (({ __typename: _t, ...repeatFields }) => repeatFields)(repeat)
            : undefined,
        subTasks: (subTasks ?? []).map(({ __typename: _t, ...task }) => task),
        ...overrides,
    };
};

export const RitualEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { ritualType: ritualTypeParam } = useParams<{ ritualType: string }>();
    const ritualType = ritualTypeParam as RitualType;
    const authData = useReactiveVar(loginData);
    const [toggling, setToggling] = useState(false);

    const navigateBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const { morningRitualId, eveningRitualId, loading: userLoading } = useCachedRitualQuestIds();
    
    const ritualId = ritualType === RitualType.Morning ? morningRitualId : eveningRitualId;
    
    const { quest: ritual, loading: ritualLoading } = useCachedQuestWithFallback(ritualId);

    const [createQuest] = useMutation(createQuestMutation, { update: createQuestCache });
    const [updateQuest] = useMutation(updateQuestMutation);
    const [updateUser] = useMutation(updateUserMutation, {
        refetchQueries: [{ query: getUserRitualsQuery }],
    });

    const isEnabled = !!ritual && ritual.status === QuestStatus.InProgress;
    const config = getRitualConfig(t)[ritualType];

    const reenableRitual = useCallback(async () => {
        if (!ritual) return;
        await updateQuest({
            variables: toUpdateQuestVariables(ritual, {
                status: QuestStatus.InProgress,
                dueDate: getToday().toDate(),
            }),
        });
    }, [ritual, updateQuest]);

    const createFirstRitual = useCallback(async () => {
        const categoryId = getSystemCategoryId(authData?.userId);
        if (!categoryId) return;

        const today = getToday().toDate();
        const result = await createQuest({
            variables: {
                name: config.title,
                description: t('ritual.dailyDescription'),
                categoryId,
                ritualType,
                subTasks: [],
                dueDate: today,
                timing: false,
                timedInterval: 0,
                timerValueInMs: 0,
                difficulty: QuestDifficulty.Easy,
                repeat: {
                    query: 'daily',
                    repeatingType: RepeatingType.Day,
                    interval: 1,
                    weekDays: [],
                    monthDays: [],
                },
            },
        });

        const newRitualId = result.data?.createQuest?.id;
        if (newRitualId) {
            await updateUser({
                variables: {
                    data: ritualType === RitualType.Morning 
                        ? { morningRitualId: newRitualId }
                        : { eveningRitualId: newRitualId },
                },
            });
        }
    }, [authData?.userId, config, ritualType, createQuest, updateUser, t]);

    const disableRitual = useCallback(async () => {
        if (!ritual) return;
        await updateQuest({
            variables: toUpdateQuestVariables(ritual, {
                status: QuestStatus.Done,
            }),
        });
    }, [ritual, updateQuest]);

    const handleToggleRitual = useCallback(async (enabled: boolean) => {
        setToggling(true);
        try {
            if (enabled) {
                if (ritualId && ritual) {
                    await reenableRitual();
                } else {
                    await createFirstRitual();
                }
            } else {
                await disableRitual();
            }
        } finally {
            setToggling(false);
        }
    }, [ritualId, ritual, reenableRitual, createFirstRitual, disableRitual]);

    const handleAddTask = useCallback(() => {
        if (!isEnabled) return;
        const path = ritualTaskEditPath.replace(':ritualType', ritualType).replace('/:taskIndex?', '');
        navigate(path);
    }, [navigate, ritualType, isEnabled]);

    const handleEditTask = useCallback((index: number) => {
        const path = ritualTaskEditPath.replace(':ritualType', ritualType).replace(':taskIndex?', String(index));
        navigate(path);
    }, [navigate, ritualType]);

    const isLoading = userLoading || ritualLoading;

    if (isLoading) {
        return <RitualEditPageSkeleton />;
    }

    return (
        <Page
            back={true}
            insetEnabled={false}
            footer={
                <ButtonDock
                    secondary={{ text: t('actions.cancel'), onClick: navigateBack, navigation: 'back' }}
                    main={isEnabled ? { text: t('ritual.addTask'), onClick: handleAddTask } : undefined}
                />
            }
        >
            <Stack alignItems="center" justifyContent="center" minHeight="var(--tg-viewport-content-safe-area-inset-top)">
                <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '8px',
                            backgroundColor: config.iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CommonIcon icon={config.icon} type="outlined" size={20} color="white" />
                    </Box>
                    <Text>{config.title}</Text>
                </Stack>
            </Stack>
            <Stack height="100%" gap="15px" paddingTop="10px" paddingLeft="10px" paddingRight="10px" sx={{ overflow: 'auto', paddingBottom: miniButtonDockScrollBottomPad }}>
                <Box sx={{ borderRadius: '14px', overflow: 'hidden', ...liquidGlassRowShadowSx }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderRadius: 'inherit',
                            px: '16px',
                            py: '12px',
                            ...liquidGlassSurfaceFillSx,
                        }}
                    >
                        <Text>{t('ritual.enabled')}</Text>
                        <Switch checked={isEnabled} onChange={handleToggleRitual} disabled={toggling} />
                    </Box>
                </Box>
                {isEnabled && ritual && (
                    <Box sx={{ borderRadius: '14px', overflow: 'hidden', ...liquidGlassRowShadowSx }}>
                        <Box sx={{ borderRadius: 'inherit', ...liquidGlassSurfaceFillSx }}>
                            {ritual.subTasks?.map((task, index) => (
                                <RitualTaskItem
                                    key={index}
                                    name={task.name}
                                    taskType={task.taskType}
                                    showDivider={index > 0}
                                    onClick={() => handleEditTask(index)}
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Stack>
        </Page>
    );
};
